import { Observable } from 'rxjs';
import { AuthService } from './auth.service';
import { AUTHORIZATION_BEARER_TOKEN_PREFIX } from '@ee/common/constants';

export interface SseStreamEvent {
  type: string;
  data: any;
}

/**
 * POSTs `body` to `url` and parses the response as a server-sent-event stream, emitting one
 * value per event. Not `EventSource`-based: the backend requires a POST body and a bearer
 * Authorization header, neither of which `EventSource` supports.
 */
export function createSseStream(url: string, body: unknown, authService: AuthService): Observable<SseStreamEvent> {
  return new Observable(observer => {
    const abortController = new AbortController();

    authService.getAccessToken().then(token => {
      if (observer.closed) return;
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `${AUTHORIZATION_BEARER_TOKEN_PREFIX}${token}`;
      }

      fetch(url, { method: 'POST', headers, body: JSON.stringify(body), signal: abortController.signal }).then(async response => {
        if (!response.ok) {
          const errorBody = await response.text().catch(() => '');
          try {
            const parsed = JSON.parse(errorBody);
            observer.error({ status: response.status, message: parsed.message || parsed.detail || `HTTP ${response.status}` });
          } catch {
            observer.error({ status: response.status, message: `HTTP ${response.status}` });
          }
          return;
        }
        if (!response.body) {
          observer.error({ status: 0, message: 'Streaming response not supported by this browser' });
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let eventName = '';
        let eventData = '';

        const emit = () => {
          if (!eventData || observer.closed) {
            eventName = '';
            eventData = '';
            return;
          }
          try {
            observer.next({ type: eventName || 'message', data: JSON.parse(eventData) });
          } catch {
            observer.next({ type: eventName || 'message', data: eventData });
          }
          eventName = '';
          eventData = '';
        };

        // Per the SSE spec, only a single leading space after the colon is stripped from a
        // field's value — the rest of the value (including other whitespace) is significant.
        const fieldValue = (raw: string): string => (raw.charAt(0) === ' ' ? raw.slice(1) : raw);

        const consumeLines = (lines: string[]) => {
          for (const rawLine of lines) {
            const line = rawLine.replace(/\r$/, '');
            if (line.startsWith('event:')) {
              eventName = fieldValue(line.slice(6));
            } else if (line.startsWith('data:')) {
              const dataContent = fieldValue(line.slice(5));
              eventData = eventData ? eventData + '\n' + dataContent : dataContent;
            } else if (line === '') {
              emit();
            }
          }
        };

        try {
          while (true) {
            if (observer.closed) {
              await reader.cancel();
              break;
            }
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });

            const lines = buffer.split('\n');
            buffer = lines.pop()!;
            consumeLines(lines);
          }

          if (!observer.closed) {
            if (buffer) consumeLines(buffer.split('\n'));
            emit();
            observer.complete();
          }
        } finally {
          reader.releaseLock();
        }
      }).catch(err => {
        if (err?.name !== 'AbortError') observer.error(err);
      });
    }).catch(err => observer.error(err));

    return () => abortController.abort();
  });
}
